import { Pencil, Plus, Search, Sliders, Star, StarOff, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '../../../components/EmptyState.js';
import { GroupedListSkeleton } from '../../../components/Skeletons.js';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { ApiError } from '../../../lib/api-client.js';
import { useLicense } from '../../common/license-context.js';
import { KpiChip } from '../components/KpiChip.js';
import { KpiEditorDialog, type KpiEditorValues } from '../components/KpiEditorDialog.js';
import {
  useCreateKpiMetric,
  useDeleteKpiMetric,
  useKpiMetrics,
  useKpiTypes,
  useUpdateKpiMetric,
} from '../hooks.js';
import { asKpiMetric, asKpiType, type KpiMetric, type KpiType } from '../types.js';

/**
 * KPI metrics catalog. Mirrors the legacy
 * `wf-audit/src/slices/kpi/pages/metrics.tsx` — a grouped list where
 * each metric sits under its owning KPI type. Untyped metrics land
 * in a trailing `No type` bucket. Search narrows the displayed
 * metrics by `displayName` / `internalName`.
 *
 * Inline-edit affordances: each row surfaces hover-only pencil + trash
 * + star buttons. Pencil opens `KpiEditorDialog`, trash opens a delete
 * confirmation, star sets (or, on the current main metric, removes) the
 * license's main metric. The current main metric carries a persistent
 * "Main metric" badge.
 */
export function KpiMetricsPage(): JSX.Element {
  const { data: metricRows = [], isLoading, error } = useKpiMetrics();
  const { data: typeRows = [] } = useKpiTypes();
  const { details: license, updateMainMetric } = useLicense();
  const mainMetricId = license?.mainMetricId ?? null;
  const [search, setSearch] = useState('');
  const [mainMetricPending, setMainMetricPending] = useState(false);

  const types = useMemo(() => typeRows.map(asKpiType), [typeRows]);
  const metrics = useMemo(() => metricRows.map(asKpiMetric), [metricRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return metrics;
    return metrics.filter(
      (m) =>
        m.displayName.toLowerCase().includes(q) || (m.internalName ?? '').toLowerCase().includes(q),
    );
  }, [metrics, search]);

  const grouped = useMemo(() => groupByType(filtered, types), [filtered, types]);

  const create = useCreateKpiMetric();
  const update = useUpdateKpiMetric();
  const remove = useDeleteKpiMetric();

  const [editor, setEditor] = useState<{
    open: boolean;
    target: KpiMetric | null;
  }>({ open: false, target: null });
  const [deleting, setDeleting] = useState<KpiMetric | null>(null);

  const openCreate = (): void => setEditor({ open: true, target: null });
  const openEdit = (m: KpiMetric): void => setEditor({ open: true, target: m });
  const closeEditor = (): void => setEditor({ open: false, target: null });

  const onSubmit = async (values: KpiEditorValues): Promise<void> => {
    if (values.kind !== 'metric') return;
    const payload = {
      displayName: values.displayName,
      ...(values.internalName ? { internalName: values.internalName } : {}),
      color: values.color,
      icon: values.icon,
      ...(values.kpiTypeId !== null ? { kpiTypeId: values.kpiTypeId } : {}),
    };
    if (editor.target) {
      await update.mutate({ id: editor.target.id, input: payload });
    } else {
      await create.mutate(payload);
    }
    closeEditor();
  };

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    await remove.mutate(deleting.id);
    setDeleting(null);
  };

  const onToggleMainMetric = async (metric: KpiMetric): Promise<void> => {
    const makeMain = metric.id !== mainMetricId;
    setMainMetricPending(true);
    try {
      await updateMainMetric(makeMain ? metric.id : null);
      toast.success(
        makeMain ? `"${metric.displayName}" is now the main metric` : 'Main metric removed',
      );
    } catch (err) {
      // The API explains failures in its JSON body (`{ error: '...' }`);
      // `ApiError.message` is only the generic "API PATCH … failed: 4xx".
      const apiBody = err instanceof ApiError ? (err.body as { error?: unknown } | null) : null;
      const reason =
        typeof apiBody?.error === 'string'
          ? apiBody.error
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error(`Could not update the main metric: ${reason}`);
    } finally {
      setMainMetricPending(false);
    }
  };

  const initial: KpiEditorValues = editor.target
    ? {
        kind: 'metric',
        displayName: editor.target.displayName,
        internalName: editor.target.internalName ?? '',
        color: editor.target.color,
        icon: editor.target.icon,
        kpiTypeId: editor.target.kpiTypeId,
      }
    : {
        kind: 'metric',
        displayName: '',
        internalName: '',
        color: '#3b82f6',
        icon: 'bar-chart',
        kpiTypeId: null,
      };

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 pb-2 pt-6">
        <div>
          <h1 className="text-2xl font-light text-foreground">KPI Metrics</h1>
          <p className="text-sm text-foreground/60">
            Per-license catalog of value-tracking metrics. Group them via{' '}
            <Link to="/kpi/types" className="underline">
              KPI Types
            </Link>
            .
          </p>
        </div>
        <Button
          type="button"
          onClick={openCreate}
          className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
        >
          <Plus className="mr-1.5 h-4 w-4" /> New metric
        </Button>
      </header>

      <div className="flex items-center gap-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 py-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40" />
          <Input
            type="search"
            placeholder="Search metrics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-foreground/15 bg-foreground/5 pl-9 text-foreground placeholder:text-foreground/40 focus-visible:ring-white/30"
          />
        </div>
        <span className="text-xs text-foreground/50">
          {filtered.length} of {metrics.length}
        </span>
      </div>

      <div className="px-6 py-4">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            Could not load KPI metrics: {error.message}
          </div>
        ) : isLoading ? (
          <GroupedListSkeleton groups={2} itemsPerGroup={3} />
        ) : filtered.length === 0 ? (
          metrics.length === 0 ? (
            <EmptyState
              icon={Sliders}
              tint="violet"
              title="No KPI metrics defined yet"
              description="Metrics are the leaf values scenarios surface (Hours saved, $ recovered, etc.). Define one, attach a type, then point a scenario at it."
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No metrics match your search"
              description={
                <>
                  {metrics.length} metric{metrics.length === 1 ? '' : 's'} hidden — clear the search
                  to see everything.
                </>
              }
            />
          )
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => (
              <section
                key={group.key}
                className="rounded-md border border-foreground/10 bg-foreground/[0.02]"
              >
                <header className="flex items-center gap-3 border-b border-foreground/10 px-4 py-2">
                  {group.type ? (
                    <KpiChip icon={group.type.icon} color={group.type.color} size="sm" />
                  ) : (
                    <span className="h-6 w-6" />
                  )}
                  <h2 className="text-sm font-semibold text-foreground">
                    {group.type ? group.type.name : 'No type'}
                  </h2>
                  <span className="text-xs text-foreground/40">({group.metrics.length})</span>
                </header>
                <ul className="divide-y divide-white/5">
                  {group.metrics.map((metric) => (
                    <li key={metric.id} className="group flex items-center gap-3 px-4 py-2.5">
                      <KpiChip icon={metric.icon} color={metric.color} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {metric.displayName}
                        </div>
                        {metric.internalName ? (
                          <div className="truncate font-mono text-xs text-foreground/40">
                            {metric.internalName}
                          </div>
                        ) : null}
                      </div>
                      {group.type ? (
                        <span
                          className="rounded px-2 py-0.5 text-xs"
                          style={{
                            backgroundColor: `${group.type.color}26`,
                            color: group.type.color,
                          }}
                        >
                          {group.type.name}
                        </span>
                      ) : null}
                      {metric.id === mainMetricId ? (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                          <Star className="h-3 w-3 fill-current" />
                          Main metric
                        </span>
                      ) : null}
                      <div className="hidden gap-1 group-hover:flex">
                        <button
                          type="button"
                          onClick={() => void onToggleMainMetric(metric)}
                          disabled={mainMetricPending}
                          aria-label={
                            metric.id === mainMetricId
                              ? `Remove ${metric.displayName} as main metric`
                              : `Set ${metric.displayName} as main metric`
                          }
                          title={
                            metric.id === mainMetricId
                              ? 'Remove as main metric'
                              : 'Set as main metric'
                          }
                          className="rounded p-1 text-foreground/60 transition-colors hover:bg-foreground/10 hover:text-foreground disabled:opacity-50"
                        >
                          {metric.id === mainMetricId ? (
                            <StarOff className="h-3.5 w-3.5" />
                          ) : (
                            <Star className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(metric)}
                          aria-label={`Edit ${metric.displayName}`}
                          title="Edit"
                          className="rounded p-1 text-foreground/60 transition-colors hover:bg-foreground/10 hover:text-foreground"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleting(metric)}
                          aria-label={`Delete ${metric.displayName}`}
                          title="Delete"
                          className="rounded p-1 text-foreground/60 transition-colors hover:bg-rose-500/20 hover:text-rose-800 dark:hover:text-rose-200"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <KpiEditorDialog
        open={editor.open}
        onOpenChange={(open) => (open ? null : closeEditor())}
        initial={initial}
        availableTypes={types}
        onSubmit={onSubmit}
        title={editor.target ? `Edit "${editor.target.displayName}"` : 'New KPI metric'}
        submitting={create.isPending || update.isPending}
        error={(editor.target ? update.error : create.error) ?? undefined}
      />

      {deleting ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setDeleting(null)}
        >
          <div
            className="max-w-md rounded-md border border-foreground/15 bg-[color:var(--surface-overlay)] p-6 text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium">Delete KPI metric?</h3>
            <p className="mt-2 text-sm text-foreground/70">
              You're about to delete <span className="font-semibold">"{deleting.displayName}"</span>
              . Scenarios still referencing this metric will show "Unknown metric" until they're
              re-attached.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleting(null)}
                className="border-foreground/15 text-foreground/70"
                disabled={remove.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void onConfirmDelete()}
                disabled={remove.isPending}
                className="bg-rose-500 text-white hover:bg-rose-600"
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface KpiGroup {
  key: string;
  type: KpiType | null;
  metrics: ReadonlyArray<KpiMetric>;
}

function groupByType(
  metrics: ReadonlyArray<KpiMetric>,
  types: ReadonlyArray<KpiType>,
): ReadonlyArray<KpiGroup> {
  const byType = new Map<number | 'none', KpiMetric[]>();
  for (const m of metrics) {
    const key = m.kpiTypeId ?? ('none' as const);
    const arr = byType.get(key);
    if (arr) arr.push(m);
    else byType.set(key, [m]);
  }
  const groups: KpiGroup[] = [];
  for (const t of types) {
    const arr = byType.get(t.id);
    if (arr && arr.length > 0) groups.push({ key: `t-${t.id}`, type: t, metrics: arr });
  }
  const orphans = byType.get('none');
  if (orphans && orphans.length > 0) groups.push({ key: 'none', type: null, metrics: orphans });
  return groups;
}
